import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Lightning, ShieldCheck, Heart, Globe } from "@phosphor-icons/react";
import StatsCounter from "../components/sections/StatsCounter";
import CTABanner from "../components/sections/CTABanner";
import useSEO from "../lib/useSEO";

const TIMELINE = [
    { y: "1995", t: "Founded in Singapore", d: "Windermere opens its first chartering desk at Kim Seng Promenade, focused on Southeast Asia bulk." },
    { y: "2003", t: "Tanker Division Launched", d: "Expansion into clean and dirty product chartering, building long-term partnerships with regional refiners." },
    { y: "2010", t: "Offshore Marine Support", d: "Acquisition of AHTS and PSV tonnage to support upstream operators in the SCS and Gulf of Thailand." },
    { y: "2016", t: "Newbuild Programme", d: "Initiated a 12-vessel newbuild programme across Korean and Chinese yards." },
    { y: "2021", t: "Global Expansion", d: "Operational hubs activated in Rotterdam, Houston, Dubai and Mumbai." },
    { y: "2024", t: "Digital Operations", d: "Roll-out of the Windermere Voyage Cloud — live demurrage, fixture and ESG reporting." },
];

const LEADERS = [
    { name: "Hasebe Fusamitsu", role: "Director", bio: "" },
    { name: "", role: "Director of Operations", bio: "" },
    { name: "", role: "Head of Newbuilds & Repairs", bio: "" },
    { name: "", role: "Chief Commercial Officer", bio: "" },
];

const VALUES = [
    { Icon: ShieldCheck, t: "Safety First, Always", d: "Zero tolerance for compromise on the lives of our crew or partners." },
    { Icon: Lightning, t: "Operational Discipline", d: "Process maturity and post-fixture transparency in every voyage." },
    { Icon: Heart, t: "Partnership", d: "We build relationships, not transactions — across decades of charters." },
    { Icon: Globe, t: "Stewardship", d: "Maritime sustainability, decarbonization and ESG governance." },
];

export default function About() {
    useSEO({
        title: "About — Windermere Shipping",
        description: "Three decades of disciplined chartering, marine logistics and shipbuilding from Singapore.",
    });

    return (
        <>
            {/* Hero */}
            <section className="relative pt-36 md:pt-44 pb-20 md:pb-28 bg-[#040914] border-b border-white/5 overflow-hidden">
                <div className="absolute inset-0 wms-radial pointer-events-none" />
                <div className="wms-container relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <span className="wms-overline">About — Company</span>
                        <h1 className="mt-6 font-display font-light text-[clamp(2.4rem,6vw,5rem)] leading-[1.05] tracking-tight">
                            Three decades.
                            <br /> One disciplined <span className="italic text-[#48CAE4]">maritime house.</span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-white/60 text-base md:text-lg leading-relaxed">
                        Windermere Shipping Pte Ltd was founded to bring discipline, clarity and global reach to commercial maritime operations from Singapore — one of the world's most strategic ports.
                    </p>
                </div>
            </section>

            {/* Mission / Vision */}
            <section className="py-24 md:py-32 bg-[#040914]">
                <div className="wms-container grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
                    {[
                        { t: "Mission", d: "To deliver dependable, transparent and disciplined marine transportation across the world's commercial trade lanes — with safety, stewardship and operational integrity." },
                        { t: "Vision", d: "To be the most trusted independent maritime house operating out of Singapore, recognized for the depth of our fleet, the rigor of our desks and the longevity of our partnerships." },
                    ].map((b) => (
                        <div key={b.t} className="bg-[#040914] p-10 md:p-14">
                            <span className="wms-overline">{b.t}</span>
                            <p className="mt-6 font-display font-light text-2xl md:text-3xl leading-snug tracking-tight text-white">
                                {b.d}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Values */}
            <section className="py-24 md:py-32 bg-[#0B1324]">
                <div className="wms-container">
                    <span className="wms-overline">Corporate Values</span>
                    <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight">What we stand for.</h2>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
                        {VALUES.map(({ Icon, t, d }, i) => (
                            <div key={t} data-testid={`value-${i}`} className="bg-[#0B1324] p-8 hover:bg-[#101a30] transition-colors">
                                <Icon size={28} weight="duotone" className="text-[#48CAE4]" />
                                <h3 className="mt-6 font-display text-xl">{t}</h3>
                                <p className="mt-3 text-sm text-white/55 leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="py-24 md:py-32 bg-[#040914]">
                <div className="wms-container">
                    <span className="wms-overline">Timeline</span>
                    <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight">A maritime house in motion.</h2>

                    <div className="mt-14 relative">
                        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10" />
                        <ul className="space-y-12">
                            {TIMELINE.map((t, idx) => (
                                <motion.li
                                    key={t.y}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 0.5, delay: idx * 0.05 }}
                                    className={`relative pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-16 items-start`}
                                    data-testid={`timeline-${idx}`}
                                >
                                    <div className={`${idx % 2 === 0 ? "md:text-right md:pr-12" : "md:col-start-2 md:pl-12"}`}>
                                        <div className="font-display text-3xl text-[#48CAE4]">{t.y}</div>
                                        <div className="mt-2 font-display text-xl text-white">{t.t}</div>
                                        <p className="mt-2 text-white/60 text-sm md:text-base leading-relaxed">{t.d}</p>
                                    </div>
                                    <span className="absolute left-3 md:left-1/2 top-2 -translate-x-1/2 w-3 h-3 bg-[#48CAE4] border-2 border-[#040914]" />
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Leadership */}
            <section className="py-24 md:py-32 bg-[#0B1324]">
                <div className="wms-container">
                    <span className="wms-overline">Leadership</span>
                    <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight">The desk that runs the desks.</h2>

                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
                        {LEADERS.map((l, i) => {
                            const initials = l.name ? l.name.split(" ").map((s) => s[0]).join("") : "—";
                            return (
                                <div key={l.role} data-testid={`leader-${i}`} className="bg-[#0B1324] p-7 hover:bg-[#101a30] transition-colors group">
                                    <div className="aspect-[3/4] overflow-hidden bg-[#040914] mb-6">
                                        <div className="w-full h-full bg-gradient-to-br from-[#0B1324] via-[#101a30] to-[#040914] flex items-end p-5">
                                            <span className="font-display text-5xl text-white/15 select-none">{initials}</span>
                                        </div>
                                    </div>
                                    <div className="font-display text-lg text-white min-h-[1.75rem]">
                                        {l.name || <span className="text-white/30 italic text-base">To be announced</span>}
                                    </div>
                                    <div className="text-xs tracking-[0.22em] uppercase text-[#48CAE4] mt-1">{l.role}</div>
                                    {l.bio && <p className="mt-4 text-sm text-white/55 leading-relaxed">{l.bio}</p>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <StatsCounter />
            <CTABanner />
        </>
    );
}
